<template>
  <div class="student-assignments-page">
    <!-- Header with just Viva logo -->
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
      </div>
      <ProfileDropdown
        :student-name="studentName"
        :is-assignment="true"
        current-portal="assignment"
      />
    </div>

    <!-- Course Info -->
    <div class="instructor-info">
      <h2>Course: {{ courseTitle }}</h2>
      <p v-if="courseTerm || courseYear">{{ courseTerm }} {{ courseYear }}</p>
      <h1 class="viva-page-title">ASSIGNMENTS</h1>
    </div>

    <!-- Assignment Cards Grid -->
    <div class="viva-card-grid">
      <!-- Loading state -->
      <div v-if="loading" class="empty-state">
        <i class="glyphicon glyphicon-refresh spinning"></i>
        <h3>Loading assignments...</h3>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="empty-state">
        <i class="glyphicon glyphicon-exclamation-sign"></i>
        <h3>Error</h3>
        <p>{{ error }}</p>
        <button class="viva-btn viva-btn-orange" @click="loadAssignments">Try Again</button>
      </div>

      <!-- Empty state -->
      <div v-else-if="assignments.length === 0" class="empty-state">
        <i class="glyphicon glyphicon-tasks"></i>
        <h3>No Assignments</h3>
        <p>This course doesn't have any assignments yet.</p>
      </div>

      <!-- Assignment cards -->
      <div
        v-for="assignment in assignments"
        :key="assignment.id"
        class="viva-card viva-card-orange"
        @click="viewAssignment(assignment.id)"
      >
        <div class="card-title">{{ assignment.title }}</div>

        <!-- Status badge -->
        <div class="card-status" :class="statusClass(assignment.status)">
          {{ statusLabel(assignment.status) }}
        </div>

        <!-- Due date -->
        <div class="card-date" v-if="assignment.due_date">
          <i class="fa fa-calendar"></i>
          <span>Due: {{ formatDate(assignment.due_date) }}</span>
        </div>

        <!-- Submission date if completed -->
        <div class="card-date submitted" v-if="assignment.submission_date">
          <i class="fa fa-check-circle"></i>
          <span>Submitted: {{ formatDate(assignment.submission_date) }}</span>
        </div>
      </div>
    </div>

    <!-- Back Link -->
    <div class="back-section">
      <a @click="backToDashboard" class="back-link" style="cursor: pointer;">
        <span>←</span> Back to MY COURSES
      </a>
    </div>
  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js';
import { handleApiError } from '../utils/errorHandler.js';
import ProfileDropdown from '../components/ProfileDropdown.vue';
import { getStatusData } from '../utils/statusCache.js';

export default {
  name: 'StudentCourseAssignments',
  components: {
    ProfileDropdown
  },

  data() {
    return {
      assignments: [],
      courseTitle: '',
      courseTerm: '',
      courseYear: '',
      loading: true,
      error: null,
      studentName: ''
    };
  },

  async mounted() {
    await this.loadAssignments();
    await this.fetchStudentName();
  },

  methods: {
    fetchStudentName() {
      const statusData = getStatusData();
      if (statusData && statusData.student_name !== undefined) {
        this.studentName = statusData.student_name;
      }
    },

    async loadAssignments() {
      this.loading = true;
      this.error = null;

      try {
        const courseId = this.$route.params.id;

        const response = await apiClient.get(
          `/api/viva/student/course/${courseId}/assignments/`
        );

        if (response.data) {
          this.assignments = response.data.assignments;
          this.courseTitle = response.data.course.title;
          this.courseTerm = response.data.course.term;
          this.courseYear = response.data.course.year;
        }
      } catch (error) {
        console.error('Failed to load assignments:', error);
        this.error = handleApiError(error, this.$router, 'Failed to load assignments.');
      } finally {
        this.loading = false;
      }
    },

    viewAssignment(assignmentId) {
      // Set loading immediately when card is clicked
      this.$root.$emit('show-loading');
      this.$router.push({
        name: 'student-assignment-detail',
        params: { id: assignmentId }
      });
    },

    backToDashboard() {
      // Set loading immediately when back button is clicked
      this.$root.$emit('show-loading');
      this.$router.push({
        name: 'student-dashboard'
      });
    },

    statusClass(status) {
      return {
        'status-completed': status === 'completed',
        'status-available': status === 'available'
      };
    },

    statusLabel(status) {
      const labels = {
        'completed': 'Completed',
        'available': 'Available'
      };
      return labels[status] || status;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.student-assignments-page {
  min-height: 100vh;
  background-color: var(--viva-cream);
  padding-bottom: 50px;
}

.instructor-info p {
  color: var(--viva-grey);
  font-size: 16px;
  margin-top: 10px;
}

.viva-card-grid {
  padding-top: 10px;
  margin-left: 30px;
  margin-right: 30px;
  padding-left: 30px;
  padding-right: 30px;
}

.card-status {
  margin-top: 15px;
  padding: 6px 12px;
  border-radius: 20px;
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-completed {
  background-color: rgba(128, 194, 151, 0.2);
  color: var(--viva-green);
}

.status-available {
  background-color: rgba(236, 142, 0, 0.2);
  color: var(--viva-orange);
}

.card-date {
  margin-top: 12px;
  font-size: 14px;
  color: var(--viva-grey);
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-date.submitted {
  color: var(--viva-green);
}

.card-date i {
  font-size: 14px;
}
</style>
